// plans.ts — Single source of truth for KaziAtlas plan definitions.
// Used by: PlanGate, SuperAdminBillingPage, admin-billing-api types.

export type PlanKey = 'free' | 'team' | 'pro' | 'enterprise';
export type BillingStatus = 'trial' | 'paid' | 'unpaid' | 'cancelled';

// Internal free key is displayed as Starter; enterprise remains for legacy contracts.
export interface Plan {
  key: PlanKey;
  label: string;
}

export const PLANS: Plan[] = [
  { key: 'free', label: 'Starter' },
  { key: 'team', label: 'Team' },
  { key: 'pro', label: 'Pro' },
  { key: 'enterprise', label: 'Enterprise' },
];

/** Returns the plan definition for a given key (defaults to free). */
export function getPlan(key: string): Plan {
  return PLANS.find(p => p.key === key) ?? PLANS[0];
}

/**
 * Feature keys that require at least Pro plan.
 * Used by PlanGate to decide whether to render children or an upgrade prompt.
 */
export const PRO_FEATURES = new Set([
  'ai_copilot', 'time_machine', 'audit_log',
  'custom_roles', 'workload', 'dependency_impact', 'status_report',
]);
export const ENTERPRISE_FEATURES = new Set(['sso', 'dedicated_support', 'on_premise']);

/** Returns the minimum plan required for a feature key. */
export function requiredPlan(featureKey: string): PlanKey {
  if (ENTERPRISE_FEATURES.has(featureKey)) return 'enterprise';
  if (PRO_FEATURES.has(featureKey)) return 'pro';
  return 'free';
}

/** Returns true if planKey is >= required. */
export function planIncludes(planKey: PlanKey, featureKey: string): boolean {
  const order: PlanKey[] = ['free', 'team', 'pro', 'enterprise'];
  return order.indexOf(planKey) >= order.indexOf(requiredPlan(featureKey));
}

// Approved workspace prices. Backend validates the configured Stripe price
// before opening Checkout. Legacy subscriptions keep their agreed terms.
export const WORKSPACE_PLANS = [
 { key: 'free', label: 'Starter', monthly: 0, projects: 3, people: 3 },
 { key: 'team', label: 'Team', monthly: 19, projects: 10, people: 10 },
 { key: 'pro', label: 'Pro', monthly: 49, projects: null, people: 25 },
] as const;
